import { defineComponent as _defineComponent } from 'vue'
import LoadingDot from '@/components/loaders/LoadingDot.vue';
import Tag from '@/components/shift/Tag.vue';
import i18n from '@/i18n';
import { dateTimeFormats } from '@/lang/dateTimeFormats';
import { Icon, IconType } from '@/lib/enum/Icon';
import { userCan } from '@/lib/permission/userCan';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { presentShiftTimes } from '@/lib/schedule-v2/scheduleV2Functions';
import {
  getShiftTagNames,
  shiftJobRoleIsVisible,
  tagVisibilityFilter,
} from '@/lib/schedule-v2/shiftV2Functions';
import store from '@/store';
import { getEntity } from '@/util/entityFunctions';
import { isCurrentEmployee } from '@/util/permissionFunctions';
import { computed } from 'vue';
import {
  Employee,
  ScheduledBreak,
  ScheduleSettings,
  Shift,
} from '../../../../api/v1';

enum SummaryRowInfoTypeEnum {
  Date = 'Date',
  Duration = 'Duration',
  JobRole = 'JobRole',
  Breaks = 'Breaks',
  Location = 'Location',
  Tags = 'Tags',
  Notes = 'Notes',
}

type SummaryRowType = {
  infoType: SummaryRowInfoTypeEnum;
  iconClass: IconType;
  info?: string;
};

/* PROPS */

export default /*#__PURE__*/_defineComponent({
  __name: 'ShiftDetails',
  props: {
    shift: null,
    employee: { default: null },
    shortList: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { shift: Shift, employee: Employee | null, shortList: boolean }



/* DATA */
const jobRoles = realtimeQuery(Entity.JobRole).fetch();
const locations = realtimeQuery(Entity.Location).fetch();
const tags = realtimeQuery(Entity.Tag).fetch();

/* COMPUTED */
const scheduleSettings = computed<ScheduleSettings>(
  () => store.state.settings.scheduleSettings,
);

const initialising = computed<boolean>(
  () => jobRoles.isLoading || locations.isLoading || tags.isLoading,
);

const jobRoleName = computed<string>(() => {
  if (shiftJobRoleIsVisible(props.shift.showJobRole)) {
    return getEntity(props.shift.jobRoleId, jobRoles.data).name;
  }
  return '';
});

const locationName = computed<string>(() => {
  return getEntity(props.shift.locationId, locations.data).name;
});

const isLoggedInEmployee = computed<boolean>(() =>
  props.employee ? isCurrentEmployee(props.employee) : false,
);

const tagNames = computed<string[]>(() => {
  return getShiftTagNames(
    props.shift,
    tags.data.filter(tagVisibilityFilter(userCan.manageShifts())),
  );
});

const showTagsForEmployee = computed<boolean>(
  () =>
    tagNames.value.length &&
    (isLoggedInEmployee.value ||
      scheduleSettings.value.employeesCanViewShiftTags ||
      props.shift.open),
);

const summaryRows = computed<SummaryRowType[]>(() => [
  ...(props.shortList
    ? []
    : [
        {
          infoType: SummaryRowInfoTypeEnum.Date,
          iconClass: Icon.CalendarXmark,
          info: i18n.d(
            props.shift.startsAt,
            dateTimeFormats.weekdayDayMonthLongYear,
          ),
        },
        {
          infoType: SummaryRowInfoTypeEnum.Duration,
          iconClass: Icon.Clock,
          info: presentShiftTimes(props.shift),
        },
      ]),
  ...(jobRoleName.value
    ? [
        {
          infoType: SummaryRowInfoTypeEnum.JobRole,
          iconClass: Icon.Briefcase,
          info: jobRoleName.value,
        },
      ]
    : []),
  {
    infoType: SummaryRowInfoTypeEnum.Breaks,
    iconClass: Icon.BurgerSoda,
  },
  {
    infoType: SummaryRowInfoTypeEnum.Location,
    iconClass: Icon.LocationDot,
    info: locationName.value,
  },
  // display for employees without manageShifts permission as list items
  ...(userCan.manageShifts()
    ? []
    : [
        ...(showTagsForEmployee.value
          ? [{ infoType: SummaryRowInfoTypeEnum.Tags, iconClass: Icon.Tags }]
          : []),
        ...(props.shift.notes && (isLoggedInEmployee.value || props.shift.open)
          ? [
              {
                infoType: SummaryRowInfoTypeEnum.Notes,
                iconClass: Icon.Comment,
                info: props.shift.notes,
              },
            ]
          : []),
      ]),
]);

/* METHODS */
const generateBreakRowInfo = ({
  durationInMinutes,
  paid,
  startsAt,
}: ScheduledBreak): string => {
  const duration = i18n.tc('unitWithAmount.min', durationInMinutes, {
    n: durationInMinutes,
  });
  const breakWord = i18n.tc('label.break').toLowerCase();
  const breakType = paid
    ? scheduleSettings.value?.paidBreakName ??
      i18n.tc('label.paid').toLowerCase()
    : scheduleSettings.value?.unpaidBreakName ??
      i18n.tc('label.unpaid').toLowerCase();

  let result = `${duration} ${breakWord} (${breakType})`;
  if (startsAt) {
    const startTime = i18n.d(startsAt, dateTimeFormats.hourMinute);
    result = `${result} @ ${startTime}`;
  }

  return result;
};

return { __sfc: true,SummaryRowInfoTypeEnum, props, jobRoles, locations, tags, scheduleSettings, initialising, jobRoleName, locationName, isLoggedInEmployee, tagNames, showTagsForEmployee, summaryRows, generateBreakRowInfo, LoadingDot, Tag, userCan }
}

})